// frontend/src/hooks/useSessionWatcher.js
// ----------------------------------------------------
// useSessionWatcher – monitors Zerodha login session silently
// Redirects to /login only if session is invalid or missing
// ----------------------------------------------------

import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../config";

export default function useSessionWatcher(intervalMs = 60_000) {
  const navigate = useNavigate();
  const location = useLocation();
  const intervalRef = useRef(null);

  useEffect(() => {
    let destroyed = false;

    // ✅ Attach JWT token globally if present
    const token = localStorage.getItem("jwt");
    if (token && !axios.defaults.headers.common["Authorization"]) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }

    const checkSession = async () => {
      try {
        // 🚦 Skip session check on callback route (Zerodha redirect in progress)
        if (location.pathname.startsWith("/kite-callback")) return;

        // 🩺 Check if backend is even reachable before calling /auth/session
        try {
          await axios.get(`${API_BASE}`);
        } catch {
          console.warn("⚠️ Backend not reachable → skipping session check this round");
          return;
        }

        // 🧠 Now check active session (with JWT header if available)
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { data } = await axios.get(`${API_BASE}/auth/session`, { headers });

        if (!data?.ok) {
          console.warn("⚠️ Session invalid or expired — redirecting to /login");

          // clear stale token
          localStorage.removeItem("jwt");
          delete axios.defaults.headers.common["Authorization"];

          if (
            location.pathname !== "/login" &&
            location.pathname !== "/register" &&
            !location.pathname.startsWith("/kite-callback")
          ) {
            setTimeout(() => {
              if (!destroyed) navigate("/login", { replace: true });
            }, 500);
          }
        } else {
          // ✅ Active session (supports both JWT and legacy Kite sessions)
          const user =
            data?.user?.email ||
            data?.session?.user_id ||
            data?.email ||
            "anonymous";

          console.log("✅ Session active for:", user);
        }

      } catch (err) {
        // 🚫 Handle network errors silently (avoid spam redirects)
        if (!destroyed) {
          console.warn("❌ Session check skipped due to:", err.message);
        }
      }
    };

    // 🔹 Run immediately on mount
    checkSession();

    // 🔹 Schedule background checks
    intervalRef.current = setInterval(checkSession, intervalMs);

    return () => {
      destroyed = true;
      clearInterval(intervalRef.current);
    };
  }, [navigate, location, intervalMs]);
}
